import type { ReactNode } from 'react';
import { Box, Button, Chip, Stack, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BodyText, CaptionText } from '../../../shared/ui/typography';
import { ArrowBack as BackIcon, Close as CloseIcon } from '@mui/icons-material';
import { borderRadius, layoutGap } from '../../../theme/theme';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import HelpTooltipTrigger from '../../../shared/ui/common/HelpTooltipTrigger';
import { useNavigation } from '../../../shared/lib/hooks/useNavigation';
import { usePcfValidationStore } from '../../../shared/stores/usePcfValidationStore';
import type { ValidationIssueFamilyTargetPage } from '../lib/validationIssueFamilies';
import { validationActionTooltips } from '../../../shared/config/actionHelpCatalog';
import { validationCopy } from '../copy/validationCopy';

interface ValidationNavigationNoticeProps {
  applicableTargetPages: ValidationIssueFamilyTargetPage[];
  sx?: Record<string, unknown>;
  action?: ReactNode;
}

export default function ValidationNavigationNotice({
  applicableTargetPages,
  sx,
  action,
}: ValidationNavigationNoticeProps) {
  const theme = useTheme();
  const { currentScreen, navigateTo } = useNavigation();
  const issueNavigationContext = usePcfValidationStore((state) => state.issueNavigationContext);
  const clearIssueNavigationContext = usePcfValidationStore((state) => state.clearIssueNavigationContext);
  const requestWorkspaceTab = usePcfValidationStore((state) => state.requestWorkspaceTab);
  const copy = validationCopy.navigationNotice;

  if (
    !issueNavigationContext ||
    !applicableTargetPages.includes(issueNavigationContext.targetPage)
  ) {
    return null;
  }

  const handleReturnToValidation = () => {
    if (issueNavigationContext.sourceScreen === 'mappingConfiguration') {
      requestWorkspaceTab(issueNavigationContext.sourceWorkspaceTab || 'validation');

      if (currentScreen !== 'mappingConfiguration') {
        navigateTo('mappingConfiguration');
      }

      return;
    }

    if (currentScreen !== issueNavigationContext.sourceScreen) {
      navigateTo(issueNavigationContext.sourceScreen);
    }
  };

  return (
    <Box
      sx={{
        mx: 1.5,
        mt: 1,
        px: 1.25,
        py: 1,
        borderRadius: `${borderRadius.md}px`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        ...sx,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={layoutGap.row}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
      >
        <Stack spacing={layoutGap.loft} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={layoutGap.comfort} useFlexGap flexWrap="wrap" alignItems="center">
            <Chip
              label={copy.chipLabel}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.625rem',
                fontWeight: 700,
                borderRadius: `${borderRadius.sm}px`,
                backgroundColor: alpha(theme.palette.primary.main, 0.14),
                color: theme.palette.primary.light,
              }}
            />
            <HelpTooltipTrigger
              label={copy.helpLabel}
              help={validationActionTooltips.guidedAction}
              placement="bottom-start"
            />
            <Chip label={issueNavigationContext.targetLabel} size="small" variant="outlined" />
            <CaptionText color="text.secondary">
              {copy.affected(issueNavigationContext.affectedCount)}
            </CaptionText>
          </Stack>

          <BodyText secondary>
            {issueNavigationContext.title}
          </BodyText>

          <CaptionText color="text.secondary">
            {issueNavigationContext.summary}
          </CaptionText>

          {issueNavigationContext.affectedItems.length > 0 && (
            <Stack direction="row" spacing={layoutGap.blend} useFlexGap flexWrap="wrap">
              {issueNavigationContext.affectedItems.slice(0, 4).map((item) => (
                <Chip
                  key={item}
                  label={item}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: '0.625rem' }}
                />
              ))}
              {issueNavigationContext.affectedItems.length > 4 && (
                <CaptionText color="text.secondary" sx={{ alignSelf: 'center' }}>
                  +{issueNavigationContext.affectedItems.length - 4} more
                </CaptionText>
              )}
            </Stack>
          )}

          {issueNavigationContext.targetPage === 'mapping' &&
            (issueNavigationContext.actionParams?.mappingReferences?.length ?? 0) > 0 && (
              <Stack spacing={layoutGap.snug}>
                {issueNavigationContext.actionParams!.mappingReferences!.map((reference) => (
                  <CaptionText
                    key={`${reference.nodeType}:${reference.nodeName ?? reference.label}`}
                    color="text.secondary"
                  >
                    {reference.label}: {reference.attributes.join(', ')}
                  </CaptionText>
                ))}
              </Stack>
            )}
        </Stack>

        <Stack direction="row" spacing={layoutGap.comfort} flexWrap="wrap" useFlexGap>
          {action}
          <ActionHelpTooltip help={validationActionTooltips.backToValidation}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={handleReturnToValidation}
            >
              {copy.back}
            </Button>
          </ActionHelpTooltip>
          <ActionHelpTooltip help={validationActionTooltips.dismissFixFocus}>
            <Button
              size="small"
              variant="text"
              startIcon={<CloseIcon />}
              onClick={clearIssueNavigationContext}
            >
              {copy.dismiss}
            </Button>
          </ActionHelpTooltip>
        </Stack>
      </Stack>
    </Box>
  );
}
